import {
  Button,
  FileUpload,
  Flex,
  Label,
  Select,
  Text,
  type FileObjectType,
} from "@jsq/ds/core";
import { SPACING } from "~/styles/variables";
import { DATA_PACKAGE_HIERARCHY_ACCEPTED_FILE_TYPES } from "../acceptedFileTypes";
import { buildDataPackageSections } from "../dataPackageSections";
import { HierarchyPreviewPane } from "../HierarchyPreviewPane";
import { type HierarchyUpload } from "../hooks";
import * as sharedStyles from "../styles_vanilla.css";
import type { RejectedUploadFile } from "../uploadFiles";

type DataPackageOption = {
  id: number;
  name: string;
  arenaId?: number | null;
};

type CommonProps = {
  upload: HierarchyUpload;
  isDownloading: boolean;
  isSubmitting: boolean;
  onDownload: () => void;
  onSave: () => void;
};

type CreateProps = CommonProps & {
  mode: "create";
  dataPackages: DataPackageOption[];
  arenaName?: string;
  selectedTemplateId: string | undefined;
  onTemplateChange: (value: string | string[] | number | number[]) => void;
  onBack: () => void;
};

type UpdateProps = CommonProps & {
  mode: "update";
};

export type HierarchyStepProps = CreateProps | UpdateProps;

export const HierarchyStep = (props: HierarchyStepProps) => (
  <>
    {props.mode === "create" ? <CreateSidebar {...props} /> : <UpdateSidebar {...props} />}
    <HierarchyPreviewPane
      isUploading={props.upload.isUploading}
      previewData={props.upload.previewData}
      rowErrors={props.upload.rowErrors}
      nodeCount={props.upload.nodeCount}
      preSignedErrorReportUrl={props.upload.preSignedErrorReportUrl}
    />
  </>
);

const CreateSidebar = ({
  upload,
  dataPackages,
  arenaName,
  selectedTemplateId,
  isDownloading,
  isSubmitting,
  onTemplateChange,
  onDownload,
  onSave,
  onBack,
}: CreateProps) => {
  const packageSections = buildDataPackageSections(dataPackages, arenaName);

  return (
    <Flex
      flexDirection="column"
      gap={SPACING.LARGE}
      className={sharedStyles.hierarchySidebarPanel}
    >
      <Flex flexDirection="column" gap={SPACING.X_SMALL}>
        <Text variant="h3">Upload hierarchy</Text>
        <Text variant="secondary-md">
          Download a hierarchy template, make changes, and then upload it back to preview and
          save.
        </Text>
      </Flex>

      <Flex flexDirection="column" gap={SPACING.X_SMALL}>
        <Select
          label="Hierarchy template"
          data-testid="hierarchy-template-select"
          placeholder="Select hierarchy"
          value={selectedTemplateId}
          onChange={onTemplateChange}
          full
        >
          {packageSections.map((section) => (
            <Select.Section key={section.key} name={section.name}>
              {section.packages.map((pkg) => (
                <Select.Option key={pkg.id} value={pkg.id.toString()}>
                  {pkg.name}
                </Select.Option>
              ))}
            </Select.Section>
          ))}
        </Select>
        <Button
          displayAsLink
          icon="download"
          iconPosition="left"
          onClick={onDownload}
          disabled={!selectedTemplateId}
          isLoading={isDownloading}
        >
          Download selected template
        </Button>
      </Flex>

      <HierarchyFileUpload upload={upload} isSubmitting={isSubmitting} />

      <Flex justifyContent="space-between" className={sharedStyles.sidebarFooter}>
        <Button variant="neutral" onClick={onBack} disabled={isSubmitting}>
          Back
        </Button>
        <Button
          variant="primary"
          disabled={!upload.uploadedFile || upload.isUploading || !upload.previewData}
          isLoading={isSubmitting}
          onClick={onSave}
        >
          Save data package
        </Button>
      </Flex>
    </Flex>
  );
};

const UpdateSidebar = ({
  upload,
  isDownloading,
  isSubmitting,
  onDownload,
  onSave,
}: UpdateProps) => (
  <Flex flexDirection="column" className={sharedStyles.hierarchySidebarPanel}>
    <Flex flexDirection="column" gap={SPACING.LARGE}>
      <Flex flexDirection="column" gap={SPACING.X_SMALL}>
        <Text variant="h3">Upload new hierarchy</Text>
        <Text variant="secondary-md">
          Download the existing hierarchy, make changes, and then upload it back to preview and
          save
        </Text>
        <Button
          displayAsLink
          icon="download"
          iconPosition="left"
          onClick={onDownload}
          isLoading={isDownloading}
          data-testid="download-current-hierarchy"
        >
          Download current hierarchy
        </Button>
      </Flex>
      <HierarchyFileUpload
        upload={upload}
        isSubmitting={isSubmitting}
        label="Upload new hierarchy"
      />
    </Flex>

    <Flex justifyContent="right" className={sharedStyles.sidebarFooter}>
      <Button
        variant="primary"
        disabled={!upload.uploadedFile || upload.isUploading || !upload.previewData}
        isLoading={isSubmitting}
        onClick={onSave}
        data-testid="save-update-btn"
      >
        Save update
      </Button>
    </Flex>
  </Flex>
);

type HierarchyFileUploadProps = {
  upload: HierarchyUpload;
  isSubmitting: boolean;
  label?: string;
};

const HierarchyFileUpload = ({
  upload,
  isSubmitting,
  label = "Upload hierarchy",
}: HierarchyFileUploadProps) => {
  const initialFiles: FileObjectType[] = upload.uploadedFile ? [upload.uploadedFile] : [];
  const uploadProps: {
    initialFiles?: FileObjectType[];
    serverRejectedFiles?: RejectedUploadFile[];
  } = {};
  if (initialFiles.length > 0) uploadProps.initialFiles = initialFiles;
  if (upload.serverRejectedFiles) uploadProps.serverRejectedFiles = upload.serverRejectedFiles;

  return (
    <Flex flexDirection="column" gap={SPACING.X_SMALL}>
      <Label>{label}</Label>
      <FileUpload
        isFullWidth
        canAllowMultiple={false}
        onFileSelected={upload.handleFileSelected}
        acceptedFileTypes={DATA_PACKAGE_HIERARCHY_ACCEPTED_FILE_TYPES}
        isDisabled={upload.isUploading || isSubmitting}
        data-testid="hierarchy-file-upload"
        {...uploadProps}
      />
    </Flex>
  );
};
